import axios from 'axios';

// Get backend URL with fallback and validation
const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (!backendUrl) {
    console.warn('REACT_APP_BACKEND_URL is not configured, using origin fallback');
    // Fall back to same origin (works for Emergent deployments where frontend/backend share origin)
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/api`;
    }
    return '/api'; // Fallback for SSR or relative URLs
  }
  
  return backendUrl + '/api';
};

const API_URL = getApiUrl();

// Create axios instance with interceptors for auth
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Request interceptor to always attach fresh auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhance error with more context
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      if (status === 401) {
        console.error('Unauthorized - token may be invalid or expired');
        // Optionally clear token and redirect to login
        // localStorage.removeItem('token');
        // window.location.href = '/login';
      } else if (status === 403) {
        console.error('Forbidden - insufficient permissions');
      } else if (status >= 500) {
        console.error('Server error:', error.response.data);
      }
    } else if (error.request) {
      // Request made but no response (network error, CORS, etc.)
      console.error('Network error - no response received:', error.message);
      if (error.message.includes('Network Error')) {
        error.friendlyMessage = 'Unable to connect to server. Please check your connection.';
      }
    } else {
      // Error in request setup
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Also set on default axios for any legacy direct axios calls
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Helper to check if API is configured
export const isApiConfigured = () => {
  return !!process.env.REACT_APP_BACKEND_URL;
};

// Helper to get current API URL (for debugging)
export const getConfiguredApiUrl = () => API_URL;

export const api = {
  // RFPs (Phase 1)
  getRFPs: () => apiClient.get('/rfps'),
  getRFP: (rfpId) => apiClient.get(`/rfps/${rfpId}`),
  createRFP: (data) => apiClient.post('/rfps', data),
  updateRFPStatus: (rfpId, status, adminNotes, estimatedCost) =>
    apiClient.patch(`/rfps/${rfpId}/status`, null, {
      params: { status, admin_notes: adminNotes, estimated_cost: estimatedCost }
    }),

  // Projects (Phase 1)
  getProjects: () => apiClient.get('/projects'),
  getProject: (projectId) => apiClient.get(`/projects/${projectId}`),
  createProject: (data) => apiClient.post('/projects', data),
  updateProjectPhase: (projectId, phase) =>
    apiClient.patch(`/projects/${projectId}/phase`, null, {
      params: { phase }
    }),
  addTeamMember: (projectId, userId, role) =>
    apiClient.patch(`/projects/${projectId}/team`, null, {
      params: { user_id: userId, role }
    }),

  // Invoices (Phase 2)
  getInvoices: () => apiClient.get('/invoices'),
  getInvoice: (invoiceId) => apiClient.get(`/invoices/${invoiceId}`),

  // Payments (Phase 2)
  initializePayment: (invoiceId) =>
    apiClient.post('/payments/initialize', null, {
      params: { invoice_id: invoiceId }
    }),
  verifyPayment: (reference) =>
    apiClient.post(`/payments/verify/${reference}`),

  // Subscriptions (Phase 2)
  getSubscriptionPlans: () => apiClient.get('/subscription-plans'),
  subscribe: (planCode) =>
    apiClient.post('/subscriptions/subscribe', null, {
      params: { plan_code: planCode }
    }),
  getMySubscription: () => apiClient.get('/subscriptions/my-subscription'),
  cancelSubscription: () => apiClient.post('/subscriptions/cancel'),

  // Financial Dashboard (Phase 2)
  getFinancialDashboard: () => apiClient.get('/financial/dashboard'),

  // Stats
  getStats: () => apiClient.get('/stats'),

  // Gallery
  getGallery: (category) => 
    apiClient.get('/gallery', category ? { params: { category } } : {}),
  createGalleryItem: (data) => apiClient.post('/gallery', data),

  // Services (Legacy - keeping for compatibility)
  getServices: () => apiClient.get('/services'),
  createService: (data) => apiClient.post('/services', data),

  // AI Tools (Phase 3)
  generateEventConcept: (data) => apiClient.post('/ai/generate-concept', data),
  analyzeBudget: (data) => apiClient.post('/ai/analyze-budget', data),
  recommendVendors: (eventType, location, budgetRange) =>
    apiClient.post('/ai/recommend-vendors', null, {
      params: { event_type: eventType, location, budget_range: budgetRange }
    }),
  generateProposal: (rfpId) =>
    apiClient.post('/ai/generate-proposal', null, {
      params: { rfp_id: rfpId }
    }),
  generatePostEventInsights: (projectId, attendance, feedbackSummary) =>
    apiClient.post('/ai/post-event-insights', null, {
      params: { project_id: projectId, attendance, feedback_summary: feedbackSummary }
    }),
  optimizeProgramFlow: (eventType, durationHours, activities) =>
    apiClient.post('/ai/optimize-program-flow', null, {
      params: { event_type: eventType, duration_hours: durationHours, activities }
    }),

  // Enterprise Features (Phase 4)
  getLegalContent: (type) => apiClient.get(`/legal/${type}`),
  updateGDPRConsent: (consent) =>
    apiClient.post('/users/me/consent', null, {
      params: { consent }
    }),
  getAuditLogs: (userId, limit = 50) =>
    apiClient.get('/audit-logs', { params: { user_id: userId, limit } }),
  
  // Users
  getUsers: () => apiClient.get('/users'),

  // Stripe Payments (New)
  createStripeCheckout: (data) => apiClient.post('/payments/stripe/checkout/invoice', data),
  createSubscriptionCheckout: (data) => apiClient.post('/payments/stripe/checkout/subscription', data),
  getStripePaymentStatus: (sessionId) => apiClient.get(`/payments/stripe/status/${sessionId}`),
  getPaymentTransactions: (limit = 50) => apiClient.get('/payments/transactions', { params: { limit } }),
  processRefund: (transactionId, reason) => apiClient.post(`/payments/refund/${transactionId}`, null, { params: { reason } }),

  // Paystack Payments
  initializePaystackPayment: (invoiceId) => apiClient.post('/payments/paystack/initialize', null, { params: { invoice_id: invoiceId } }),
  verifyPaystackPayment: (reference) => apiClient.get(`/payments/paystack/verify/${reference}`),

  // Invoice Management
  downloadInvoicePdf: (invoiceId) => apiClient.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  updateInvoiceStatus: (invoiceId, status) => apiClient.patch(`/invoices/${invoiceId}/status`, null, { params: { status } }),
  createInvoice: (data) => apiClient.post('/invoices', data),

  // Financial Dashboard
  getRevenueByClient: () => apiClient.get('/financial/revenue-by-client'),
  getRevenueByMonth: (months = 6) => apiClient.get('/financial/revenue-by-month', { params: { months } }),

  // Approvals (Enterprise)
  submitApproval: (data) => apiClient.post('/approvals/submit', data),
  getProjectApprovals: (projectId, status, approvalType) => 
    apiClient.get(`/approvals/project/${projectId}`, { params: { status, approval_type: approvalType } }),
  getPendingApprovals: () => apiClient.get('/approvals/pending'),
  getApproval: (approvalId) => apiClient.get(`/approvals/${approvalId}`),
  reviewApproval: (approvalId, data) => apiClient.post(`/approvals/${approvalId}/review`, data),
  resubmitApproval: (approvalId, updatedData, notes) => 
    apiClient.post(`/approvals/${approvalId}/resubmit`, updatedData, { params: { notes } }),
  getApprovalStats: () => apiClient.get('/approvals/stats/summary'),

  // Analytics
  getAnalyticsDashboard: (timeRange = 'month') => 
    apiClient.get('/analytics/dashboard', { params: { time_range: timeRange } }),
  getProjectProfitability: () => apiClient.get('/analytics/projects/profitability'),
  getEventsByStage: () => apiClient.get('/analytics/events/by-stage'),
  getAIUsageByRole: () => apiClient.get('/analytics/ai-usage/by-role'),
  getFeedbackSummary: () => apiClient.get('/analytics/feedback/summary'),

  // Feedback
  submitFeedback: (data) => apiClient.post('/feedback/submit', data),
  getMyFeedback: () => apiClient.get('/feedback/my-feedback'),

  // Public API
  getFeaturedExperiences: () => apiClient.get('/public/experiences/featured'),
  getPublicExperiences: (category) => 
    apiClient.get('/public/experiences', category ? { params: { category } } : {}),
  getPublicStats: () => apiClient.get('/public/stats'),
  getUpcomingTeasers: () => apiClient.get('/public/upcoming'),
  requestNotification: (teaserId, email) => 
    apiClient.post(`/public/upcoming/${teaserId}/notify`, null, { params: { email } }),
  subscribeForNotifications: (data) => apiClient.post('/public/subscribe', data),
  getMethodology: () => apiClient.get('/public/methodology'),
  submitPublicInquiry: (data) => apiClient.post('/public/inquiry', data),

  // Admin Inquiry Management (CRM-Lite)
  getAdminInquiries: (params) => apiClient.get('/admin/inquiries', { params }),
  getInquiryDetail: (inquiryId) => apiClient.get(`/admin/inquiries/${inquiryId}`),
  updateInquiry: (inquiryId, updates) => apiClient.patch(`/admin/inquiries/${inquiryId}`, updates),
  markInquiryRead: (inquiryId) => apiClient.post(`/admin/inquiries/${inquiryId}/mark-read`),
  convertInquiryToClient: (inquiryId, createProject = false, projectName = '') => 
    apiClient.post(`/admin/inquiries/${inquiryId}/convert`, null, { 
      params: { create_project: createProject, project_name: projectName } 
    }),
  exportInquiriesCSV: (params) => apiClient.get('/admin/inquiries/export/csv', { 
    params, 
    responseType: 'blob' 
  }),
  testGsheetsConnection: () => apiClient.post('/admin/inquiries/test-gsheets'),
  getGsheetsStatus: () => apiClient.get('/admin/inquiries/gsheets-status'),
};

export default api;
